/**
 * Keyboard focus for sidebar items that move or leave. Pinning, moving and
 * unpinning remount an item, which takes focus with it to the page's body;
 * each item's focus target carries its key in this attribute so focus can
 * find the item again, or its neighbour.
 */
export const PIN_KEY_ATTRIBUTE = 'data-pin-key';

function targets(root: ParentNode): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(`[${PIN_KEY_ATTRIBUTE}]`)
  );
}

/**
 * Where focus goes once the item with `key` moves or leaves: the item
 * itself wherever it lands, else the next item after it, else the one
 * before, as the sidebar reads now.
 */
export function focusCandidates(key: string, root: ParentNode): string[] {
  const keys = targets(root).map(
    (element) => element.getAttribute(PIN_KEY_ATTRIBUTE) ?? ''
  );
  const index = keys.indexOf(key);
  if (index === -1) return [key];
  return [key, ...keys.slice(index + 1), ...keys.slice(0, index).reverse()];
}

/** Focuses the first candidate on the page; false when none is. */
export function focusFirst(
  candidates: readonly string[],
  root: ParentNode
): boolean {
  const byKey = new Map(
    targets(root).map((element) => [
      element.getAttribute(PIN_KEY_ATTRIBUTE),
      element,
    ])
  );
  for (const key of candidates) {
    const target = byKey.get(key);
    if (target) {
      target.focus();
      return true;
    }
  }
  return false;
}

/** The key of the sidebar item that has focus, or null. */
export function focusedPinKey(): string | null {
  const active = document.activeElement;
  return active instanceof HTMLElement
    ? active.getAttribute(PIN_KEY_ATTRIBUTE)
    : null;
}

/**
 * Focuses the candidates once the change on its way has rendered, unless
 * focus went somewhere on its own meanwhile.
 */
export function refocusAfterRender(
  candidates: readonly string[],
  root: () => ParentNode
): void {
  requestAnimationFrame(() => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    focusFirst(candidates, root());
  });
}
